import React, { useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import styled from 'styled-components'
import logo from '../Assets/despar.jpg'
import { colors, bp } from '../theme'
import { authApi } from '../api'
import { setAuth, isAuthenticated } from '../auth'

const Main = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: radial-gradient(1000px 500px at 15% -10%, ${colors.greenLight} 0%, transparent 55%),
    ${colors.mist};
`

const Container = styled.div`
  width: 100%;
  max-width: 420px;
  background: ${colors.white};
  border: 1px solid ${colors.line};
  border-radius: 18px;
  padding: 2.5rem;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);

  @media (max-width: ${bp.mobile}) {
    padding: 2rem 1.5rem;
  }
`

const LogoImg = styled.img`
  height: 54px;
  width: auto;
  display: block;
  margin: 0 auto 1.25rem;
`

const Title = styled.h1`
  text-align: center;
  color: ${colors.ink};
  margin: 0 0 0.4rem;
  font-size: 1.6rem;
`

const Subtitle = styled.p`
  text-align: center;
  color: ${colors.slate};
  margin: 0 0 1.75rem;
  line-height: 1.6;
  font-size: 0.92rem;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 0.4rem;
    font-size: 0.9rem;
    font-weight: 600;
    color: ${colors.ink};
  }

  input {
    padding: 0.85rem 1rem;
    font-size: 1rem;
    border: 1px solid ${colors.line};
    border-radius: 10px;
    outline: none;
    background: ${colors.mist};
    transition: 0.2s ease;
  }

  input:focus {
    border-color: ${colors.green};
    box-shadow: 0 0 0 3px ${colors.greenLight};
    background: ${colors.white};
  }
`

const Button = styled.button`
  padding: 0.9rem;
  font-size: 1rem;
  font-weight: 700;
  color: ${colors.white};
  background: ${colors.green};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: 0.2s ease;
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    background: ${colors.greenDark};
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const ErrorMsg = styled.div`
  background: ${colors.redLight};
  color: ${colors.red};
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  font-size: 0.88rem;
`

const Foot = styled.div`
  margin-top: 1.75rem;
  text-align: center;
  color: ${colors.slate};
  font-size: 0.8rem;
`

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from || '/dashboard'

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Already signed in? Skip the form.
  if (isAuthenticated()) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const result = await authApi.login(username, password)
      if (result?.success && result.token) {
        setAuth(result.token, result.user)
        navigate(from, { replace: true })
      } else {
        setError(result?.message || 'Invalid login credentials')
      }
    } catch (err) {
      setError(err.message || 'Unable to sign in. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Main>
      <Container>
        <LogoImg src={logo} alt="Despar Clean logo" />
        <Title>Welcome back</Title>
        <Subtitle>Sign in to the Despar Clean management dashboard.</Subtitle>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMsg>{error}</ErrorMsg>}

          <Field>
            <label htmlFor="username">Username</label>
            <input
              id="username"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
          </Field>

          <Field>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>

          <Button type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </Button>
        </Form>

        <Foot>© {new Date().getFullYear()} Desparclean Sterix Holdings Limited</Foot>
      </Container>
    </Main>
  )
}

export default Login
